import React, { useContext } from 'react'
import injectStyles from 'react-jss'
import cn from 'classnames'
import { useDispatch } from 'react-redux'

import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'

import { ButtonInline } from 'Components/Buttons/ButtonInline'
import { SnackBarContext } from 'Components/SnackBar'

import EditIcon from 'Assets/Images/icons/edit'
import DeleteIcon from 'Assets/Images/icons/cancel'
import SaveIcon from 'Assets/Images/icons/save'
import { theme } from 'Assets/Styles/theme'

import TableContainer from '@material-ui/core/TableContainer'
import Table from '@material-ui/core/Table'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import TableCell from '@material-ui/core/TableCell'
import TableBody from '@material-ui/core/TableBody'
import Checkbox from '@material-ui/core/Checkbox'

import styles from './styles.js'
import { useHandleEdit } from '../../../../useHandleEdit.js'
import { CHANNEL_SETTINGS_HEADERS } from '../../../../../../constants'
import { showReduxModal } from '../../../../../../../../Reducers/UI/actions'
import { getErrors } from './utils'

const ChannelSettingsTable = ({
  classes,
  title = '',
  countrySettings = {},
  citiesSettings = [],
  onSave = () => {},
  setCountrySettings = () => {},
  defaultCountrySettings = {},
  isEditingTable = false,
  setIsEditingTable = () => {},
}) => {
  const {
    enabledChannelPreciseLocations,
    enabledChannelBroadAreas,
    enabledChannelLetterbox,
  } = defaultCountrySettings
  const dispatch = useDispatch()

  const runSnackBar = useContext(SnackBarContext)

  const { isEdit, handleEditMode } = useHandleEdit({
    isEditingTable,
    setIsEditingTable,
  })

  const handleSave = async () => {
    const response = await onSave()
    if (!response) return
    const { success, error } = response
    if (success) {
      runSnackBar({
        type: 'success',
        msg: `${title} were saved successfully`,
      })

      handleEditMode(false)
    } else {
      runSnackBar({
        type: 'error',
        msg: `There is an error while saving ${title}.`,
        error: error,
      })
    }
  }

  const handleCancel = () => {
    setCountrySettings(prev => ({
      ...prev,
      enabledChannelPreciseLocations,
      enabledChannelBroadAreas,
      enabledChannelLetterbox,
    }))
    handleEditMode(false)
  }

  const handleCellChange = (value, key) => {
    if (countrySettings[key] && !value) {
      const channel = key === 'enabledChannelLetterbox' ? 'D2D' : 'H2H'
      dispatch(
        showReduxModal({
          title: 'Confirmation needed',
          onConfirm: () => {
            setCountrySettings(prev => ({
              ...prev,
              [key]: value,
            }))
          },
          children: (
            <div style={{ textAlign: 'center' }}>
              Are you sure that you want to disable {channel} for the entire
              country? Self-serve clients will not be able to create campaigns
              of this type.
            </div>
          ),
          confirmLabel: 'I am sure',
        }),
      )
    } else if (value) {
      const errors = getErrors({ key, countrySettings, citiesSettings })

      if (errors.length) {
        dispatch(
          showReduxModal({
            title: 'Cannot save',
            onConfirm: () => {},
            children: errors.some(error =>
              ['cantEnableH2H', 'cantEnableD2D'].includes(error),
            ) ? (
              <div style={{ textAlign: 'center' }}>
                You can't enable this channel because it is disabled in every
                city in your country. Please enable it in some cities and try
                again.
              </div>
            ) : (
              errors.map(error => (
                <div style={{ textAlign: 'center' }}>
                  Missing <strong>{error}</strong>. Please, add{' '}
                  <strong>{error}</strong> before saving
                </div>
              ))
            ),
            confirmLabel: 'OK',
            hideCancel: true,
          }),
        )
      } else {
        setCountrySettings(prev => ({
          ...prev,
          [key]: value,
        }))
      }
    }
  }

  const renderTableCell = key => {
    if (isEdit) {
      return (
        <Checkbox
          checked={countrySettings[key]}
          onChange={(event, checked) => handleCellChange(checked, key)}
        />
      )
    } else {
      return countrySettings[key] ? (
        <div className={classes.liveChannel}>LIVE</div>
      ) : (
        <div className={classes.disabledChannel}>DISABLED</div>
      )
    }
  }

  return (
    <Box
      className={classes.wrapper}
      key={title
        .toLocaleLowerCase()
        .split(' ')
        .join('-')}
    >
      <Box className={classes.header}>
        <Typography className={classes.title}>{title}</Typography>
        <Box>
          {isEdit ? (
            <Box className={classes.btnWrapper}>
              <ButtonInline
                label={'Save'}
                labelColor={theme.colors.primary}
                icon={<SaveIcon />}
                onClickHandler={handleSave}
              />
              <ButtonInline
                label={'Cancel'}
                icon={<DeleteIcon />}
                onClickHandler={handleCancel}
              />
            </Box>
          ) : (
            <ButtonInline
              label={'Edit'}
              icon={<EditIcon style={{ marginLeft: '7px' }} />}
              labelFirst
              onClickHandler={() => handleEditMode(true)}
            />
          )}
        </Box>
      </Box>
      <Box className={classes.tableItemsWrapper}>
        <Box className={classes.table}>
          <TableContainer className={classes.tableContainer}>
            <Table stickyHeader size="medium">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  {CHANNEL_SETTINGS_HEADERS.map((headCell, index) => (
                    <TableCell
                      key={headCell.label}
                      align={index === 0 ? 'left' : 'center'}
                      className={cn(classes.tableHeaderCell, {
                        [classes.borderRight]: !index,
                      })}
                    >
                      <Typography>{headCell.label}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow hover={isEdit} tabIndex={-1}>
                  {CHANNEL_SETTINGS_HEADERS.map(({ key }, index) => {
                    return (
                      <TableCell
                        align={index === 0 ? 'left' : 'center'}
                        className={cn({
                          [classes.padding4]: isEdit && index,
                          [classes.borderRight]: !index,
                        })}
                      >
                        {!index ? key : renderTableCell(key)}
                      </TableCell>
                    )
                  })}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Box>
  )
}

export default injectStyles(styles)(ChannelSettingsTable)
